import {
  ExcessTowerLayerInput,
  HasCoverageInput,
  HasEndorsementInput,
  HasEnterpriseRatingInput,
  HasEntityInput,
  HasScheduleRatingInput,
  HasSubCoverageInput,
  HasSubjectivityInput,
  IUnderwritingCoverage,
  IUnderwritingEndorsement,
  IUnderwritingQuote,
  QuoteType,
  RaterMode,
  UpsertQuoteMutationVariables,
} from '@converge/graphql';

import { IUnderwritingSocketEndorsementOutput } from '../types';
import config from '@converge/config';
import { roundNumber } from '@converge/utils';

export const getEndorsementRaterPropertyOrFallback =
  (
    raterData: IUnderwritingSocketEndorsementOutput,
    endorsement: IUnderwritingEndorsement
  ) =>
  <
    K extends
      | keyof IUnderwritingSocketEndorsementOutput = keyof IUnderwritingSocketEndorsementOutput,
    D extends IUnderwritingSocketEndorsementOutput[K] = IUnderwritingSocketEndorsementOutput[K]
  >(
    key: K
  ): D => {
    return (raterData[key] ??
      endorsement.quote[key as keyof IUnderwritingEndorsement['quote']]) as D;
  };

export interface IUnderwritingQuoteToUpsertVariablesInput {
  submissionUuid: string; // submission.uuid
  totalUserQuotes: number; // userQuotes.pageInfo.totalResults
  mode: RaterMode;
  quote: IUnderwritingQuote;
  partial?: boolean;
}

export const getModifiedPremiumFromEndorsement = (
  quoteCoverages: IUnderwritingCoverage[],
  endorsement: IUnderwritingEndorsement
) => {
  const endorsementCoverage = quoteCoverages.filter(
    (item) => item.uuid === endorsement.coverageUuid
  )[0];
  return endorsementCoverage?.endorsements?.filter(
    (end) => end.uuid === endorsement.uuid
  )[0]?.modifiedCoveragePremium;
};

export const underwritingQuoteToUpsertVariables = ({
  mode,
  quote,
  submissionUuid,
  totalUserQuotes,
  partial = config.featureFlags.enablePartialRaterSaves,
}: IUnderwritingQuoteToUpsertVariablesInput): UpsertQuoteMutationVariables => {
  const include = ({ hasChanged }: { hasChanged?: boolean }) => hasChanged;
  const includeNested = (obj: unknown): boolean => {
    if (obj === null || typeof obj !== 'object') return false;
    const candidate = obj as { hasChanged?: boolean; [key: string]: unknown };
    if (candidate.hasChanged === true) return true;
    return Object.values(candidate).some(includeNested);
  };

  const quoteCoverages = quote.coverages;
  return {
    excessTowerLayers: quote.excessTowerLayers
      .filter(includeNested)
      .map((layer): ExcessTowerLayerInput => {
        const input: ExcessTowerLayerInput = {
          position: layer.position ?? -1,
          attachmentPoint: layer.attachmentPoint,
          benchmarkPremium: layer.benchmarkPremium,
          boundIncreasedLimitFactor: layer.boundIncreasedLimitFactor,
          boundPremium: layer.boundPremium,
          boundUnderlyingPercent: layer.boundUnderlyingPercent,
          companyShare: layer.companyShare,
          limit: layer.limit,
          minimumPremium: layer.minimumPremium,
          primary: layer.primary,
          name: layer.name,
          participants: layer.participants
            .filter(includeNested)
            .map((participant): HasEntityInput => {
              return {
                policyReferenceId: participant.quote?.policyReferenceId,
                attachmentPoint: participant.quote?.attachmentPoint,
                benchmarkPremium: participant.quote?.benchmarkPremium,
                boundIncreasedLimitFactor:
                  participant.quote?.boundIncreasedLimitFactor,
                boundPremium: participant.quote?.boundPremium,
                boundUnderlyingPercent:
                  participant.quote?.boundUnderlyingPercent,
                companyShare: participant.quote?.companyShare,
                entityUuid: participant.uuid,
                limit: participant.quote?.limit,
                minimumPremium: participant.quote?.minimumPremium,
                uuid: participant.isNew
                  ? null
                  : participant.quote?.uuid ?? null,
                coverages: (participant.coverages ?? [])
                  .filter(includeNested)
                  .map((coverage): HasCoverageInput => {
                    return {
                      uuid: coverage.quote.uuid,
                      coverageUuid: coverage.uuid,
                      // endorsements: coverage.endorsements,
                      limit: coverage.quote.limit,
                      erosion: coverage.quote.erosion,
                      included: coverage.quote.included ?? false,
                      participantCoverageName:
                        coverage.quote.participantCoverageName ?? null,
                      subCoverages: (coverage.subCoverages ?? [])
                        .filter(includeNested)
                        .map((subCoverage): HasSubCoverageInput => {
                          return {
                            coverageUuid: coverage.uuid,
                            uuid: subCoverage.quote?.uuid,
                            subLimit: subCoverage.quote?.subLimit,
                            subCoverageUuid: subCoverage.uuid,
                            included: subCoverage.quote?.included ?? false,
                            erosion: subCoverage.quote?.erosion,
                            participantCoverageName:
                              subCoverage.quote.participantCoverageName ?? null,
                          };
                        }),
                    };
                  }),
              };
            }),
        };

        return input;
      }),
    enterpriseRatings: quote.enterpriseRatings
      .filter(include)
      .map((enterpriseRating): HasEnterpriseRatingInput => {
        return {
          enterpriseRatingUuid: enterpriseRating.uuid,
          uuid: enterpriseRating.quote.uuid,
          riskLevel: enterpriseRating.quote.riskLevel,
        };
      }),
    scheduleRatings: quote.scheduleRatings
      .filter((scheduleRating) => {
        return scheduleRating.hasChanged;
      })
      .map((scheduleRating): HasScheduleRatingInput => {
        const percentage = scheduleRating?.quote?.percentage;
        return {
          scheduleRatingUuid: scheduleRating.uuid,
          uuid: scheduleRating.quote.uuid,
          percentage: percentage || percentage === 0 ? percentage : null,
          // TODO: Do we need modifier?
        };
      }),
    subjectivities: quote.subjectivities
      ? quote.subjectivities
          .filter(include)
          .map((subjectivity): HasSubjectivityInput => {
            return {
              subjectivityUuid: subjectivity.uuid as string,
              included: subjectivity.quote.included ?? false,
              deadline: subjectivity.quote.deadline,
            };
          })
      : [],
    coverages: quote.coverages
      .filter(include)
      .map((coverage): HasCoverageInput => {
        return {
          basePremium:
            typeof coverage.quote.basePremium === 'string'
              ? Number(coverage.quote.basePremium)
              : coverage.quote.basePremium,
          fusionScore: coverage.quote.fusionScore,
          fusionScoreModifier: coverage.quote.fusionScoreModifier,
          selectedFusionScoreModifier:
            coverage.quote.selectedFusionScoreModifier || config.nullValue,
          ratedFusionScoreModifier: coverage.quote.ratedFusionScoreModifier,
          coverageUuid: coverage.uuid,
          hazardGroup: coverage.quote.hazardGroup,
          included: coverage.quote.included ?? false,
          participantCoverageName:
            coverage.quote.participantCoverageName ?? null,
          limit: coverage.quote.limit,
          modifiedCoveragePremium: coverage.quote.modifiedCoveragePremium,
          // TODO: use actual notes here
          notes: '',
          restorationPeriodDays: coverage.quote.restorationPeriodDays,
          retention: coverage.quote.retention,
          technicalPremium: coverage.quote.technicalPremium,
          uuid: coverage.uuid,
          waitingPeriodHours: coverage.quote.waitingPeriodHours,
          commissionAdjustmentFactor: coverage.quote.commissionAdjustmentFactor,
          enhancedMediaFactor: coverage.quote.enhancedMediaFactor,
          limitRetentionFactor: coverage.quote.limitRetentionFactor,
          policyTermFactor: coverage.quote.policyTermFactor,
          priorActsFactor: coverage.quote.priorActsFactor,
          restorationPeriodFactor: coverage.quote.restorationPeriodFactor,
          sharedCoverageAggregateFactor:
            coverage.quote.sharedCoverageAggregateFactor,
          sharedPolicyAggregateFactor:
            coverage.quote.sharedPolicyAggregateFactor,
          subCoverageFactor: coverage.quote.subCoverageFactor,
          waitingPeriodFactor: coverage.quote.waitingPeriodFactor,
          maxFusionScoreModifier: coverage.quote.maxFusionScoreModifier,
          minFusionScoreModifier: coverage.quote.minFusionScoreModifier,
          fullPriorActs: coverage.quote.fullPriorActs,
          retroactiveDate: coverage.quote.fullPriorActs
            ? 'null'
            : coverage.quote.retroactiveDate,
          waitingPeriodDays: coverage.quote.waitingPeriodDays,
          subCoverages: coverage.subCoverages
            .filter(include)
            .map((subCoverage): HasSubCoverageInput => {
              return {
                coverageUuid: coverage.uuid,
                uuid:
                  mode === RaterMode.CreateQuote
                    ? undefined
                    : subCoverage.quote?.uuid,
                included: subCoverage.quote?.included ?? false,
                subLimit: subCoverage.quote?.subLimit,
                subRetention: subCoverage.quote?.subRetention,
                subCoverageUuid: subCoverage.uuid,
                waitingPeriodHours: subCoverage.quote?.waitingPeriodHours,
                restorationPeriodDays: subCoverage.quote?.restorationPeriodDays,
                participantCoverageName:
                  subCoverage.quote?.participantCoverageName ?? null,
              };
            }),
        };
      }),
    endorsements:
      (quote.endorsements ?? [])
        .filter(include)
        .map((endorsement): HasEndorsementInput => {
          const coverageModifiedCoveragePremium = roundNumber(
            getModifiedPremiumFromEndorsement(quoteCoverages, endorsement)
          );
          const { uuid, quote } = endorsement;
          return {
            included: quote.included ?? false,
            includedInExcess: quote.includedInExcess ?? false,
            notes: '',
            limit: quote.limit,
            retention: quote.retention,
            endorsementUuid: uuid as string,
            basePremium:
              typeof quote.basePremium === 'string'
                ? Number(quote.basePremium)
                : quote.basePremium,
            fusionScoreModifier: quote.fusionScoreModifier,
            selectedFusionScoreModifier:
              quote.selectedFusionScoreModifier || config.nullValue,
            ratedFusionScoreModifier: quote.ratedFusionScoreModifier,
            hazardGroup: quote.hazardGroup,
            modifiedCoveragePremium:
              coverageModifiedCoveragePremium || quote.modifiedCoveragePremium,
            restorationPeriodDays: quote.restorationPeriodDays,
            technicalPremium: quote.technicalPremium,
            waitingPeriodHours: quote.waitingPeriodHours,
            commissionAdjustmentFactor: quote.commissionAdjustmentFactor,
            enhancedMediaFactor: quote.enhancedMediaFactor,
            limitRetentionFactor: quote.limitRetentionFactor,
            policyTermFactor: quote.policyTermFactor,
            priorActsFactor: quote.priorActsFactor,
            restorationPeriodFactor: quote.restorationPeriodFactor,
            sharedCoverageAggregateFactor: quote.sharedCoverageAggregateFactor,
            sharedPolicyAggregateFactor: quote.sharedPolicyAggregateFactor,
            subCoverageFactor: quote.subCoverageFactor,
            waitingPeriodFactor: quote.waitingPeriodFactor,
            maxFusionScoreModifier: quote.maxFusionScoreModifier,
            minFusionScoreModifier: quote.minFusionScoreModifier,
            retroactiveDate: quote.fullPriorActs ? null : quote.retroactiveDate,
            fullPriorActs: quote.fullPriorActs,
            fusionScore: quote.fusionScore,
            mediaEventCoverageOption: endorsement?.features
              ?.enableMediaEventCoverage
              ? quote.mediaEventCoverageOption
              : null,
          };
        }) ?? [],
    details: {
      name:
        mode === RaterMode.CreateQuote
          ? `Option ${(totalUserQuotes ?? 0) + 1}`
          : quote.name,
      type: mode === RaterMode.CreateQuote ? QuoteType.UserQuote : quote.type,
      uuid: mode === RaterMode.CreateQuote ? undefined : quote.uuid,
      referenceId:
        mode === RaterMode.CreateQuote ? undefined : quote.referenceId,
      quotedPremium: quote.quotedPremium,
      ratedPremium: quote.ratedPremium,
      selectedQuotedPremium: quote.selectedQuotedPremium ?? -1,
      quotedRatedRatio: quote?.quotedRatedRatio,
      limit: quote.limit,
      modifiedCoveragePremium: quote.modifiedCoveragePremium,
      order:
        mode === RaterMode.CreateQuote
          ? (totalUserQuotes ?? 0) + 1
          : quote.order,
      retention: quote.retention,
      technicalPremium: quote.technicalPremium,
      endorsementImpact: quote.endorsementImpact,
      status: quote.status,
      stcSelectedModifier: quote.stcSelectedModifier ?? config.nullValue,
      stcMinModifier: quote.stcMinModifier,
      stcMaxModifier: quote.stcMaxModifier,
      erFusionScoreModifier: quote.erFusionScoreModifier,
      erMaxFusionScoreModifier: quote.erMaxFusionScoreModifier,
      erMinFusionScoreModifier: quote.erMinFusionScoreModifier,
      srModifier: quote.srModifier,
      srPercentage: quote.srPercentage,
      minimumPremium:
        typeof quote.minimumPremium === 'string'
          ? Number(quote.minimumPremium)
          : quote.minimumPremium ?? undefined,
      erRatedFusionScoreModifier: quote.erRatedFusionScoreModifier,
      erSelectedFusionScoreModifier:
        quote.erSelectedFusionScoreModifier ?? config.nullValue,
      stcModifier: quote.stcModifier,
      stcRatedModifier: quote.stcRatedModifier,
      excessConvergeAttachmentPoint: quote.excessConvergeAttachmentPoint,
      excessConvergeIncreasedLimitFactor:
        quote.excessConvergeIncreasedLimitFactor,
      excessConvergeLimit: quote.excessConvergeLimit,
      excessConvergePartOfLimit: quote.excessConvergePartOfLimit,
      excessConvergeQuotaShare: quote.excessConvergeQuotaShare,
      excessConvergeQuotedPremium: quote.excessConvergeQuotedPremium,
      excessConvergeQuotedToBenchmarkRatio:
        quote.excessConvergeQuotedToBenchmarkRatio,
      excessConvergeRatedPremium: quote.excessConvergeRatedPremium,
      excessConvergeUnderlyingPremiumPercent:
        quote.excessConvergeUnderlyingPremiumPercent,
      excessCorrectiveFactor: quote.excessCorrectiveFactor,
      excessEndorsementImpact: quote.excessEndorsementImpact,
      excessMaxCorrectiveFactor: quote.excessMaxCorrectiveFactor,
      excessMinCorrectiveFactor: quote.excessMinCorrectiveFactor,
      excessConvergeQuotedRatedRatio: quote.excessConvergeQuotedRatedRatio,
      excessSelectedCorrectiveFactor:
        quote.excessSelectedCorrectiveFactor ?? config.nullValue,
      srExcessModifier: quote.srExcessModifier,
      srExcessPercentage: quote.srExcessPercentage,
      stcExcessMaxModifier: quote.stcExcessMaxModifier,
      stcExcessMinModifier: quote.stcExcessMinModifier,
      stcExcessModifier: quote.stcExcessModifier,
      stcExcessRatedModifier: quote.stcExcessRatedModifier,
      stcExcessSelectedModifier:
        quote.stcExcessSelectedModifier ?? config.nullValue,
      erFusionScore: quote.erFusionScore,
    },
    submissionUuid,
  };
};
